// libraries
// internationalization
import type { TFunction } from 'i18next';

// api
import type { Severity } from '@/api/generated/recalc/endpoints/v2/alarmConfiguration';

import { z } from 'zod';

export const SEVERITY_LEVELS: Severity[] = [
  'Information',
  'Trivial',
  'Minor',
  'Medium',
  'Major',
  'Critical',
];

/**
 * Shared shape for every alarm configuration create/edit form (both
 * QualityOfServiceDataStream and UsageOverTime). The dynamic `configuration`
 * object is embedded directly as the runtime zod schema built from the
 * alarm type's JSON Schema (see `schemaUtils.ts`'s
 * `buildConfigurationZodSchema`), so nested validation errors land at their
 * real `configuration.*` dot-paths (e.g. `configuration.threshold.value`)
 * without needing to manually re-run and re-map a second schema.
 */
export function createAlarmConfigurationFormSchema(
  t: TFunction,
  configurationSchema: z.ZodType<
    Record<string, unknown>,
    Record<string, unknown>
  >,
) {
  return z.object({
    title: z.string(),
    message: z.string(),
    alarmSubtype: z.string().min(1, t('input.required', { ns: 'validation' })),
    severity: z.enum(SEVERITY_LEVELS),
    dataStreamKeys: z.array(z.string()).min(
      1,
      t('alarmConfigurationForm.dataStreams.noneSelected', {
        ns: 'forms',
      }),
    ),
    configuration: configurationSchema,
  });
}

export type AlarmConfigurationFormValues = z.infer<
  ReturnType<typeof createAlarmConfigurationFormSchema>
>;
